import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useParams } from '@tanstack/react-router';
import { isEqual, sortBy, uniqBy } from 'lodash-es';

import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import CreateRelationModal from '../../components/modals/create_relation/create_relation';
import ModalsManager from '../../components/modals/modals_manager/modals_manager';
import ReverseRelationConfirmModal from '../../components/modals/reverse_relation_confirm/reverse_relation_confirm';
import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import { RELATIONS } from '../../constants/help_urls';
import {
  CREATE_RELATION_MODAL,
  REVERSE_RELATION_CONFIRM_MODAL,
} from '../../constants/modals';
import {
  assetRelationsGet,
  assetRelationsTypesGet,
} from '../../ducks/asset_relations/actions';
import { openModal } from '../../ducks/modals_manager/slice';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import { selectUserRoles } from '../../utils/get_user_roles';
import { ui } from '../../utils/helpers';
import AssetRelationsTable from './asset_relations_table/asset_relations_table';
import CollectionRelations from './collection_relations';
import {
  selectDeletedRelations,
  selectGroupedRelations,
  selectRelationsLabels,
} from './selectors';
import { searchRelatedCollections } from './thunks';

import styles from './asset_relations.module.css';

const getLabel = (labelValue: string) => {
  if (labelValue === 'SUBCLIP_PARENT') {
    return 'Subclip parent';
  }
  if (labelValue === 'SUBCLIP_SIBLING') {
    return 'Subclip sibling';
  }
  if (labelValue === 'SUBCLIP_PARENT_CHILD') {
    return 'Subclip child';
  }
  return labelValue;
};

export function AssetRelationsPanel(): ReactNode {
  const dispatch = useAppDispatch();
  const { id: assetId } = useParams({ strict: false });

  const [collections, setCollections] = useState<SearchItemEntity[]>([]);
  const [newInCollectionsIds, setNewInCollectionsIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const relations = useAppSelector((state) =>
    selectGroupedRelations(state, assetId),
  );
  const deletedRelations = useAppSelector((state) =>
    selectDeletedRelations(state, assetId),
  );
  const labels = useAppSelector(selectRelationsLabels);
  const inCollections = useAppSelector(
    (state) => state.pages.asset.asset.in_collections,
  );
  const roles = useAppSelector(selectUserRoles);

  const loadCollections = useCallback(
    async (idsAddedSinceLastLoad: string[] = []) => {
      if (!inCollections) {
        return;
      }
      const response = await dispatch(searchRelatedCollections(inCollections));
      // Search returns the elastic schemas, whose relations are nullable, and
      // SearchItemEntity is the normalized shape with them required. Nothing
      // normalizes in between here.
      // biome-ignore lint/plugin: see above.
      setCollections(response.body.objects as SearchItemEntity[]);
      setNewInCollectionsIds(idsAddedSinceLastLoad);
    },
    [inCollections],
  );

  const loadRelations = useCallback(() => {
    if (!assetId) {
      return Promise.resolve();
    }
    return Promise.all([
      dispatch(assetRelationsGet(assetId)),
      dispatch(assetRelationsTypesGet()),
    ]);
  }, [assetId]);

  const previousInCollections = useRef<string[] | null | undefined>(undefined);

  // Mount and every asset change: relations are keyed by the asset in the URL.
  // Collections are not loaded here: inCollections still belongs to the
  // outgoing asset until its payload lands, so the effect below loads them
  // once the new asset's ids arrive.
  useEffect(() => {
    previousInCollections.current = undefined;
    setCollections([]);
    setNewInCollectionsIds([]);
    setLoading(true);
    loadRelations().finally(() => {
      setLoading(false);
    });
  }, [loadRelations]);

  // Reload collections when the asset is added to or removed from one, and
  // highlight the ones that appeared since the previous load.
  useEffect(() => {
    if (isEqual(previousInCollections.current, inCollections)) {
      return;
    }
    const previous = previousInCollections.current;
    previousInCollections.current = inCollections;
    const addedIds = inCollections?.filter(
      // an empty previous list means this is the initial load, not an addition
      (id) => previous?.length && !previous.includes(id),
    );
    setLoading(true);
    loadCollections(addedIds).finally(() => {
      setLoading(false);
    });
  }, [inCollections, loadCollections]);

  const onCreate = useCallback(() => {
    dispatch(
      openModal({
        id: CREATE_RELATION_MODAL,
        contextItems: [{ id: assetId ?? '' }],
      }),
    );
  }, [assetId]);

  if (loading) {
    return <LoadingBar />;
  }

  if (!assetId) {
    return null;
  }

  const getAssetRelationTable = (
    tableItems: SearchItemEntity[],
    labelValue: string,
    key: string,
  ): ReactNode => {
    const deletedBy = deletedRelations
      .filter((item) => item.relation === key)
      .map((deleted) => deleted.id);
    const items = sortBy(
      uniqBy(
        tableItems.filter((item) => !deletedBy.includes(item.id ?? '')),
        'id',
      ),
      'title',
    );
    if (!items.length) {
      return undefined;
    }
    const label = getLabel(labelValue);
    return (
      <div key={label}>
        {label ? (
          <h3 className={styles.title} {...ui('title')}>
            {label}
          </h3>
        ) : null}
        <AssetRelationsTable items={items} label={label} sourceId={assetId} />
      </div>
    );
  };

  const getAssetRelationTables = (
    assetRelations: SearchItemEntity[],
    key: string,
  ): ReactNode => {
    const relatedTo: SearchItemEntity[] = [];
    const relatedFrom: SearchItemEntity[] = [];

    assetRelations.forEach((assetRelation) => {
      const relationList = assetRelation.relations ?? [];
      const assetRelatedTo = relationList.find(
        (item) =>
          item.related_to_asset_id === assetId && item.relation_type === key,
      );

      const assetRelatedFrom = relationList.find(
        (item) =>
          item.related_from_asset_id === assetId && item.relation_type === key,
      );

      if (assetRelatedTo) {
        relatedTo.push({ ...assetRelation, relations: [assetRelatedTo] });
      }
      if (assetRelatedFrom) {
        relatedFrom.push({ ...assetRelation, relations: [assetRelatedFrom] });
      }
    });
    const keyLabels = labels[key] || {};
    // fallback to the relation name if there is no labels
    const destinationLabel = keyLabels.destination_label || key;
    const sourceLabel = keyLabels.source_label || key;

    const destinationRelations = getAssetRelationTable(
      relatedTo,
      destinationLabel,
      key,
    );
    const sourceRelations = getAssetRelationTable(
      relatedFrom,
      destinationRelations && sourceLabel === destinationLabel
        ? ''
        : sourceLabel,
      key,
    );
    if (!destinationRelations && !sourceRelations) {
      return undefined;
    }

    return (
      <div key={key}>
        {destinationRelations}
        {sourceRelations}
      </div>
    );
  };

  const assetRelationElements = relations
    .map((relation) => getAssetRelationTables(relation.value, relation.key))
    .filter((item) => item !== undefined);

  const deletedCollectionRelations = deletedRelations
    .filter((item) => item.relation === 'COLLECTION')
    .map((item) => item.id);

  const sortedCollections = sortBy(collections, 'title');

  let content = (
    <div className={styles.wrapper}>
      {assetRelationElements}
      <CollectionRelations
        deletedItems={deletedCollectionRelations}
        collections={sortedCollections}
        sourceId={assetId}
        newInCollectionsIds={newInCollectionsIds}
      />

      {roles.canCreateAssetRelations && (
        <div className={styles.button_container}>
          <Button
            variant="success"
            noBorder
            iconName="relations"
            onClick={onCreate}
          >
            Create Relation
          </Button>
        </div>
      )}
    </div>
  );

  if (
    assetRelationElements.length <= 0 &&
    sortedCollections.length - deletedCollectionRelations.length <= 0
  ) {
    content = (
      <NoContentPanel
        title="Define relationships to link assets and keep projects organized"
        linkText="Learn more"
        linkHref={RELATIONS}
        actionText="CREATE RELATION"
        onActionClick={roles.canCreateAssetRelations ? onCreate : undefined}
      />
    );
  }

  return (
    <>
      <ModalsManager
        modals={{
          [CREATE_RELATION_MODAL]: CreateRelationModal,
          [REVERSE_RELATION_CONFIRM_MODAL]: ReverseRelationConfirmModal,
        }}
      />
      {content}
    </>
  );
}

export default AssetRelationsPanel;
